"use client";

import { ApiClient } from "@/lib/api/http";
import { ApiError } from "@/lib/api/http";
import { DeleteResponse } from "@/models/delete.model";
import { SaveResponse } from "@/models/save.model";
import {
  UserFileSortResponse,
  UserFilesPerPageResponse,
  UserInfoResponse,
  UserLocaleResponse,
  UserTranscriptionListResponse,
  UserTranscriptionSpecificResponse,
  UserTranslationListResponse,
  UserTranslationSpecificResponse,
  UserUploadedFilesResponse,
} from "@/models/user.model";
import type { SignupAttribution } from "@/utils/ads/signup-attribution";

import { JSON_HEADERS } from "../fetch";

export async function getUserInfo(api: ApiClient): Promise<UserInfoResponse | null> {
  const endpoint = "/user";

  try {
    return await api.request<UserInfoResponse>(endpoint, { auth: true, headers: JSON_HEADERS });
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) {
      return null;
    }
    console.error(error);
    throw error;
  }
}

export async function updateUserLocale(body: BodyInit, api: ApiClient) {
  const endpoint = "/user/locale";

  try {
    return await api.put<UserLocaleResponse>(endpoint, body, { auth: true, headers: JSON_HEADERS });
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export async function updateUserFileSort(body: BodyInit, api: ApiClient) {
  const endpoint = "/user/file-sort";

  try {
    return await api.put<UserFileSortResponse>(endpoint, body, { auth: true, headers: JSON_HEADERS });
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export async function updateUserFilesPerPage(body: BodyInit, api: ApiClient) {
  const endpoint = "/user/files-per-page";

  try {
    return await api.put<UserFilesPerPageResponse>(endpoint, body, { auth: true, headers: JSON_HEADERS });
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export async function getUserTranscriptionList(api: ApiClient) {
  const endpoint = "/user/transcriptions";

  try {
    return await api.request<UserTranscriptionListResponse>(endpoint, { auth: true, headers: JSON_HEADERS });
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export async function getUserSpecificTranscription(uuid_filename: string, api: ApiClient, useAuth = true) {
  const endpoint = `/user/transcriptions/${uuid_filename}`;

  try {
    return await api.get<UserTranscriptionSpecificResponse>(endpoint, { auth: useAuth, headers: JSON_HEADERS });
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export async function getUserTranslationList(api: ApiClient) {
  const endpoint = "/user/translations";

  try {
    return await api.request<UserTranslationListResponse>(endpoint, { auth: true, headers: JSON_HEADERS });
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export async function getUserSpecificTranslation(uuid_filename: string, api: ApiClient, useAuth = true) {
  const endpoint = `/user/translations/${uuid_filename}`;

  try {
    return await api.get<UserTranslationSpecificResponse>(endpoint, { auth: useAuth, headers: JSON_HEADERS });
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export async function getUserUploadedFiles(api: ApiClient) {
  const endpoint = "/user/files";

  try {
    return await api.request<UserUploadedFilesResponse>(endpoint, { auth: true, headers: JSON_HEADERS });
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export async function deleteUserUploadedFile(uuid_filename: string, api: ApiClient) {
  const endpoint = `/user/files/${uuid_filename}`;

  try {
    return await api.delete<string>(endpoint, { auth: true, headers: JSON_HEADERS });
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export async function deleteUserSpecificTranscription(body: BodyInit, api: ApiClient) {
  const endpoint = "/user/transcriptions/delete";

  try {
    return await api.post<DeleteResponse>(endpoint, body, { auth: true, headers: JSON_HEADERS });
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export async function deleteUserSpecificTranslation(body: BodyInit, api: ApiClient) {
  const endpoint = "/user/translations/delete";

  try {
    return await api.delete<DeleteResponse>(endpoint, { auth: true, headers: JSON_HEADERS, body });
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export async function saveUserSpecificTranscription(body: BodyInit, api: ApiClient) {
  const endpoint = "/user/transcriptions/save";

  try {
    return await api.post<SaveResponse>(endpoint, body, { auth: true, headers: JSON_HEADERS });
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export async function saveUserSpecificTranslation(body: BodyInit, api: ApiClient) {
  const endpoint = "/user/translations/save";

  try {
    return await api.post<SaveResponse>(endpoint, body, { auth: true, headers: JSON_HEADERS });
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export async function copyUserExampleTranscription(api: ApiClient) {
  const endpoint = "/user/transcriptions/example";

  try {
    return await api.post<{ uuid_filename: string }>(endpoint, undefined, { auth: true });
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export async function generateUserApiKey(api: ApiClient) {
  const endpoint = "/user/apikey/generate";

  try {
    return await api.post<{ api_key: string }>(endpoint, undefined, { auth: true });
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export async function renameUserFile(uuid_filename: string, body: BodyInit, api: ApiClient) {
  const endpoint = `/user/files/${uuid_filename}/rename`;

  try {
    return await api.put<{ new_name: string }>(endpoint, body, { auth: true, headers: JSON_HEADERS });
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export async function toggleTranscriptionShareable(uuid_filename: string, is_shareable: boolean, api: ApiClient) {
  const endpoint = `/user/transcriptions/${uuid_filename}/share`;

  try {
    return await api.patch<{ message: string }>(endpoint, JSON.stringify({ is_shareable: is_shareable }), {
      auth: true,
      headers: JSON_HEADERS,
    });
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export async function saveSignupAttribution(attribution: SignupAttribution, api: ApiClient) {
  return api.put<{ user_id: string; attribution: SignupAttribution }>("/user/acquisition-attribution", JSON.stringify(attribution), {
    auth: true,
    headers: JSON_HEADERS,
  });
}
